package com.gsb.vyapar.bhagya.presentation.navigation.view

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Icon
import androidx.compose.material.Surface
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.unit.dp
import com.gsb.vyapar.bhagya.presentation.Screens
import com.gsb.vyapar.bhagya.domain.Business
import com.gsb.vyapar.bhagya.domain.getDisplayName
import com.gsb.vyapar.bhagya.domain.getEmail
import com.gsb.vyapar.bhagya.domain.getId
import com.gsb.vyapar.bhagya.domain.getLatitude
import com.gsb.vyapar.bhagya.domain.getLogo
import com.gsb.vyapar.bhagya.domain.getLongitude
import com.gsb.vyapar.bhagya.domain.getPhone1
import com.gsb.vyapar.bhagya.domain.isBusinessDeleted
import com.gsb.vyapar.bhagya.interactor.event.UiEvents
import com.gsb.vyapar.bhagya.interactor.uistate.UiState
import com.gsb.vyapar.bhagya.presentation.business.util.launchBusinessDetail
import com.gsb.vyapar.bhagya.presentation.business.uistate.getBusinessUiState
import com.gsb.vyapar.bhagya.presentation.business.uistate.isFavoriteBusiness
import com.gsb.vyapar.bhagya.presentation.common.image.BusinessCardPlaceHolder
import com.gsb.vyapar.bhagya.presentation.common.image.SmoothImage
import com.gsb.vyapar.bhagya.presentation.common.text.Text14RegularBB
import com.gsb.vyapar.bhagya.presentation.common.text.Text12SemiBold
import com.gsb.vyapar.bhagya.presentation.common.text.Text24SemiBoldI
import com.gsb.vyapar.bhagya.theme.black
import com.gsb.vyapar.bhagya.theme.fav_off
import com.gsb.vyapar.bhagya.theme.fav_on
import com.gsb.vyapar.bhagya.theme.mail
import com.gsb.vyapar.bhagya.theme.white
import com.gsb.vyapar.bhagya.theme.whatsapp
import com.gsb.vyapar.bhagya.theme.call
import com.gsb.vyapar.bhagya.util.BuildConfig
import com.gsb.vyapar.bhagya.util.PLACEHOLDERIMG
import com.gsb.vyapar.bhagya.util.PlatformType
import com.gsb.vyapar.bhagya.util.value
import com.gsb.vyapar.bhagya.presentation.navigation.util.pushView
import com.gsb.vyapar.bhagya.theme.primary
import com.gsb.vyapar.bhagya.theme.view_full_map
import org.jetbrains.compose.resources.painterResource

/**
 * Represents a map-ready business entry with parsed coordinates.
 */
data class BusinessLocationPin(
    val business: Business,
    val latitude: Double,
    val longitude: Double,
)

@Composable
fun HomeBusinessMap(
    uiState: UiState,
    onTriggerEvent: (UiEvents) -> Unit,
    onInteractionChanged: (Boolean) -> Unit,
) {
    if (BuildConfig.platform() != PlatformType.ANDROID && BuildConfig.platform() != PlatformType.IOS && BuildConfig.platform() != PlatformType.JS && BuildConfig.platform() != PlatformType.DESKTOP) {
        onInteractionChanged(false)
        return
    }

    val businessLocations = uiState.businessLocationPins()

    if (businessLocations.isEmpty()) {
        onInteractionChanged(false)
        return
    }

    Text24SemiBoldI(text = "Explore Businesses on the Map", color = black)
    Spacer(Modifier.height(12.dp))
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(400.dp)
            .clip(RoundedCornerShape(12.dp))
    ) {
        PlatformBusinessMap(
            modifier = Modifier.fillMaxSize(),
            businessLocations = businessLocations,
            uiState = uiState,
            onTriggerEvent = onTriggerEvent,
            onInteractionChanged = onInteractionChanged
        )

        Surface(
            modifier = Modifier
                .align(Alignment.BottomEnd)
                .padding(12.dp)
                .size(44.dp)
                .clip(CircleShape)
                .clickable {
                    onInteractionChanged(false)
                    pushView(Screens.BusinessMap)
                },
            shape = CircleShape,
            color = white.copy(alpha = 0.95f),
            elevation = 6.dp
        ) {
            Box(contentAlignment = Alignment.Center, modifier = Modifier.fillMaxSize()) {
                Icon(
                    painter = painterResource(view_full_map),
                    contentDescription = "View full map",
                    tint = primary
                )
            }
        }
    }
}

fun UiState.businessLocationPins(): List<BusinessLocationPin> {
    return businessUiState.businessList.mapNotNull { business ->
        if (business.isBusinessDeleted()) {
            return@mapNotNull null
        }
        val latitude = business.getLatitude().toDoubleOrNull()
        val longitude = business.getLongitude().toDoubleOrNull()
        if (latitude != null && longitude != null && (latitude != 0.0 || longitude != 0.0)) {
            BusinessLocationPin(business, latitude, longitude)
        } else {
            null
        }
    }
}

@Composable
fun MapBusinessPopup(
    uiState: UiState,
    onTriggerEvent: (UiEvents) -> Unit,
    business: Business,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val uriHandler = LocalUriHandler.current
    val currentBusiness = getBusinessUiState().isBusinessFetched.value[business.getId()] ?: business
    val isFavorite = uiState.businessUiState.favorites.value.isFavoriteBusiness(currentBusiness)
    var logoImage by remember { mutableStateOf(uiState.getImage(PLACEHOLDERIMG.value())) }

    LaunchedEffect(currentBusiness.getId()) {
        val logoUrl = currentBusiness.getLogo().ifEmpty { PLACEHOLDERIMG.value() }
        uiState.getImageCallback(logoUrl) { bitmap ->
            logoImage = bitmap
        }
    }

    Box(modifier = modifier) {
        Surface(
            modifier = Modifier
                .widthIn(min = 180.dp, max = 240.dp),
            shape = RoundedCornerShape(12.dp),
            color = white,
            elevation = 10.dp
        ) {
            Column(
                modifier = Modifier
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                SmoothImage(
                    imageBitmap = logoImage,
                    width = 72.dp,
                    height = 72.dp,
                    modifier = Modifier.clickable {
                        launchBusinessDetail(currentBusiness)
                    }.clip(RoundedCornerShape(8.dp))
                )
                Spacer(Modifier.height(8.dp))
                Text12SemiBold(
                    text = currentBusiness.getDisplayName(),
                    color = black,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clickable {
                            launchBusinessDetail(currentBusiness)
                        }
                )
                Spacer(Modifier.height(12.dp))
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    BusinessCardPlaceHolder(
                        drawableResource = if (isFavorite) fav_on else fav_off,
                        width = 28.dp,
                        height = 28.dp,
                        modifier = Modifier.clickable {
                            if (!currentBusiness.isBusinessDeleted()) {
                                uiState.businessUiState.toggleFavoriteBusiness(currentBusiness)
                            }
                        }
                    )
                    BusinessCardPlaceHolder(
                        drawableResource = call,
                        width = 28.dp,
                        height = 28.dp,
                        modifier = Modifier.clickable {
                            if (!currentBusiness.isBusinessDeleted()) {
                                uriHandler.openUri("tel:${currentBusiness.getPhone1()}")
                            }
                        }
                    )
                    BusinessCardPlaceHolder(
                        drawableResource = mail,
                        width = 28.dp,
                        height = 28.dp,
                        modifier = Modifier.clickable {
                            if (!currentBusiness.isBusinessDeleted()) {
                                uriHandler.openUri("mailto:${currentBusiness.getEmail()}")
                            }
                        }
                    )
                    BusinessCardPlaceHolder(
                        drawableResource = whatsapp,
                        width = 28.dp,
                        height = 28.dp,
                        modifier = Modifier.clickable {
                            if (!currentBusiness.isBusinessDeleted()) {
                                uriHandler.openUri("https://wa.me/+91${currentBusiness.getPhone1()}")
                            }
                        }
                    )
                }
            }
        }
        Surface(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(8.dp)
                .size(26.dp)
                .clip(CircleShape)
                .clickable { onDismiss() },
            color = primary,
            shape = CircleShape,
            elevation = 6.dp
        ) {
            Box(contentAlignment = Alignment.Center, modifier = Modifier.fillMaxSize()) {
                Text14RegularBB(text = "✕", color = white)
            }
        }
    }
}

@Composable
expect fun PlatformBusinessMap(
    modifier: Modifier,
    businessLocations: List<BusinessLocationPin>,
    uiState: UiState,
    onTriggerEvent: (UiEvents) -> Unit,
    onInteractionChanged: (Boolean) -> Unit,
    enableUserLocation: Boolean = true,
    retainState: Boolean = false,
)
